import { type ClassValue, clsx } from 'clsx';
import humanizeDuration from 'humanize-duration';
import pluralize from 'pluralize';
import { twMerge } from 'tailwind-merge';
import { fromZodError, isZodErrorLike } from 'zod-validation-error';

import { env } from '@/env';

import { Topic } from '@/types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getErrorMessage(error: unknown) {
  if (error instanceof Error) {
    if (isZodErrorLike(error)) {
      return fromZodError(error).message;
    }

    return error.message;
  }

  return `Unknown error: ${error}`;
}

export function debug(...message: unknown[]) {
  // Only check VITE_ var on client side
  if (typeof window !== 'undefined') {
    if (env.VITE_DEBUG_OUTPUT) console.log('[DEBUG] ', ...message);
  } else {
    if (env.DEBUG_OUTPUT) console.log('[DEBUG] ', ...message);
  }
}

const shortEnglishHumanizer = humanizeDuration.humanizer({
  largest: 1,
  round: true,
  spacer: '',
  language: 'shortEn',
  languages: {
    shortEn: {
      y: () => 'y',
      mo: () => 'mo',
      w: () => 'w',
      d: () => 'd',
      h: () => 'h',
      m: () => 'm',
      s: () => 's',
      ms: () => 'ms',
    },
  },
});

export function shortDuration(date: Date | number | string, options?: humanizeDuration.Options) {
  const durationMs = new Date().getTime() - new Date(date).getTime();

  return shortEnglishHumanizer(durationMs, options);
}

export function pluralizeFormatted(str: string, num: number, precision = 0) {
  return `${num.toLocaleString(undefined, {
    maximumFractionDigits: precision,
  })} ${pluralize(str, num)}`;
}

export function formatNumber(num: string | number, precision = 0) {
  const number = typeof num === 'string' ? Number(num) : num;

  return number.toLocaleString(undefined, {
    maximumFractionDigits: precision,
  });
}

export const formatShortDate = (date: string | Date | number) =>
  new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

export function parseTopicName(topic: Pick<Topic, 'topic' | 'displayName'>) {
  if (!topic.topic) return { topicName: '', topicLabel: '', topicBase: '', isArchived: false };

  const topicBase = topic.topic.replace(/ \(Archived \d+\)$/, '');
  const isArchived = topic.topic.includes('Archived');

  const topicLabel =
    topic.topic.toLowerCase() !== topic.displayName?.toLowerCase()
      ? `${topic.displayName} (${topicBase})`
      : topic.displayName;

  return {
    topicName: topic.topic,
    topicLabel,
    topicBase,
    isArchived,
  };
}
